import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import _ from "lodash";
import { useCallback } from "react";

import {
  InterestTopicsQuery,
  InterestTopicsQueryVariables,
  LearnerInterestTopicsQuery,
  LearnerInterestTopicsQueryVariables,
} from "../../shared/generated/graphql";

const InterestTopics = gql`
  query InterestTopics($cursor: TopicsConnectionCursorInput) {
    interestTopics(cursor: $cursor) {
      cursor {
        popularity
        created_at
        uid
      }
      topics {
        uid
        label
        image_url
      }
    }
  }
`;

function updateQuery<T, V>(updatePath: string) {
  return (
    previousQueryResult: T,
    {
      fetchMoreResult,
    }: {
      fetchMoreResult?: T;
      variables?: V;
    },
  ) => {
    if (!fetchMoreResult) {
      return previousQueryResult;
    }
    const previousResults = _.get(previousQueryResult, updatePath);
    const newResults = _.get(fetchMoreResult, updatePath);
    const merged = _.merge({}, previousQueryResult, fetchMoreResult);
    return _.set(merged, updatePath, previousResults.concat(newResults));
  };
}

export function useInterestTopics(skip = false) {
  const { fetchMore, ...result } = useQueryWithPreviousData<
    InterestTopicsQuery,
    InterestTopicsQueryVariables
  >(InterestTopics, { skip });
  const cursor = result?.data?.interestTopics?.cursor;
  const wrappedFetchMore = useCallback(async () => {
    if (cursor) {
      await fetchMore({
        variables: {
          cursor: _.omit(cursor, "__typename"),
        },
        updateQuery: updateQuery<any, any>("interestTopics.topics"),
      });
    }
  }, [fetchMore, cursor]);
  return {
    ...result,
    hasMore: Boolean(cursor),
    topics: result?.data?.interestTopics?.topics ?? [],
    fetchMore: wrappedFetchMore,
  };
}

export const LearnerInterestTopicsFragment = gql`
  fragment LearnerInterestTopicsFragment on Child {
    uid
    name
    age
    interestTopics {
      topics {
        uid
        label
      }
    }
  }
`;

const LearnerInterestTopics = gql`
  query LearnerInterestTopics {
    currentUser {
      uid
      children {
        ...LearnerInterestTopicsFragment
      }
    }
  }
  ${LearnerInterestTopicsFragment}
`;

export function useLearnerInterestTopics(skip = false) {
  return useQueryWithPreviousData<
    LearnerInterestTopicsQuery,
    LearnerInterestTopicsQueryVariables
  >(LearnerInterestTopics, { skip });
}
